import { useState, useEffect, useMemo } from "react";
import {
  DataGrid,
  DataGridBody,
  DataGridRow,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridCell,
  TableCellLayout,
  TableColumnDefinition,
  createTableColumn,
  Button,
  SearchBox,
  Dropdown,
  Option,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Textarea,
  Spinner,
  makeStyles,
  tokens,
  Tooltip,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Regular,
  DismissCircle20Regular,
  ChevronDoubleLeft20Regular,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  ChevronDoubleRight20Regular,
} from "@fluentui/react-icons";
import { app } from "@microsoft/teams-js";
import { TeamRequest, RequestStatus, AppSettings } from "./types";
import { apiService } from "./services/api.service";
import "./styles/index.css";

interface RequestsPageProps {
  theme?: string;
}

type RequestStatusFilter = "all" | RequestStatus;

// Styles Fluent UI modernes
const useStyles = makeStyles({
  root: {
    width: '100%',
    maxWidth: '100%',
    margin: 0,
    padding: tokens.spacingVerticalXL,
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    minHeight: '100vh',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  loadingSpinner: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  loadingText: {
    marginTop: tokens.spacingVerticalL,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '200px',
    gap: tokens.spacingVerticalL,
  },
  header: {
    marginBottom: tokens.spacingVerticalXXL,
    textAlign: 'left',
  },
  headerTitle: {
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    margin: `0 0 ${tokens.spacingVerticalS} 0`,
    color: tokens.colorNeutralForeground1,
  },
  headerSubtitle: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
    margin: 0,
    lineHeight: tokens.lineHeightBase300,
  },
  filtersSection: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalXL,
    marginBottom: tokens.spacingVerticalXL,
    boxShadow: tokens.shadow8,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  filtersContainer: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
    alignItems: 'flex-end',
  },
  filterItem: {
    flex: 1,
    minWidth: '280px',
  },
  filterLabel: {
    display: 'block',
    marginBottom: tokens.spacingVerticalS,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
  },
  summary: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightMedium,
  },
  datagridContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    overflow: 'auto',
    width: '100%',
    maxWidth: '100%',
  },
  datagridWrapper: {
    width: '100%',
    minWidth: '100%',
    display: 'table',
    borderCollapse: 'collapse',
  },
  datagridCell: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    verticalAlign: 'middle',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    transition: 'background-color 0.2s ease',
    display: 'table-cell',
    width: 'auto',
  },
  datagridHeaderCell: {
    backgroundColor: tokens.colorNeutralBackground2,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground1,
    borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalL}`,
    textAlign: 'left',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    display: 'table-cell',
    width: 'auto',
  },
  rowHover: {
    display: 'table-row',
    width: '100%',
    '&:hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    }
  },
  emptyState: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: `${tokens.spacingVerticalXXXL} ${tokens.spacingHorizontalXL}`,
    textAlign: 'center',
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: tokens.spacingVerticalL,
    opacity: 0.6,
  },
  emptyTitle: {
    margin: `0 0 ${tokens.spacingVerticalM} 0`,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
  },
  emptyMessage: {
    margin: 0,
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
  },
  modalBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    zIndex: 999999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalContent: {
    maxWidth: '600px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow16,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusLarge,
    padding: tokens.spacingVerticalXXXL,
    color: tokens.colorNeutralForeground1,
  },
  modalHeader: {
    margin: `0 0 ${tokens.spacingVerticalXL} 0`,
    fontSize: tokens.fontSizeHero700,
    fontWeight: tokens.fontWeightBold,
  },
  modalBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  modalField: {
    lineHeight: tokens.lineHeightBase300,
  },
  modalFieldStrong: {
    display: 'block',
    marginBottom: tokens.spacingVerticalXS,
    color: tokens.colorNeutralForeground1,
  },
  modalActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXXXL,
    justifyContent: 'flex-end',
  },
  actionButtons: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    flexWrap: 'wrap',
  },
  actionButton: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightMedium,
    transition: 'all 0.2s ease',
  },
  statusBadge: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    whiteSpace: 'nowrap',
  },
  errorMessage: {
    backgroundColor: tokens.colorPaletteRedBackground1,
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    textAlign: 'center',
    fontWeight: tokens.fontWeightMedium,
  },
  // Boutons d'action dans le DataGrid
  actionButtonsContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalXS,
    width: '100%',
    minHeight: '40px',
    flex: 1,
  },
    actionButtonIcon: {
      minWidth: '32px',
      width: '32px',
      height: '32px',
      padding: '4px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    approveActionButton: {
      color: tokens.colorPaletteGreenForeground1,
      '&:hover': {
        backgroundColor: tokens.colorPaletteGreenBackground1,
      },
    },
    rejectActionButton: {
      color: tokens.colorPaletteRedForeground1,
      '&:hover': {
        backgroundColor: tokens.colorPaletteRedBackground1,
      },
    },
    cancelActionButton: {
      color: tokens.colorPaletteYellowForeground1,
      '&:hover': {
        backgroundColor: tokens.colorPaletteYellowBackground1,
      },
    },
    detailsActionButton: {
      color: tokens.colorNeutralForeground2,
      '&:hover': {
        backgroundColor: tokens.colorNeutralBackground3,
      },
    },
    // Styles pour le modal
    dialogSurface: {
      maxWidth: '600px',
      width: '90%',
      maxHeight: '90vh',
      overflowY: 'auto',
    },
    dialogTitle: {
      fontSize: tokens.fontSizeHero700,
      fontWeight: tokens.fontWeightBold,
      marginBottom: tokens.spacingVerticalL,
    },
    dialogContent: {
      padding: tokens.spacingVerticalL,
    },
    detailsGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: tokens.spacingVerticalL,
      marginBottom: tokens.spacingVerticalXL,
    },
    detailItem: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXS,
    },
    detailItemFull: {
      gridColumn: '1 / -1',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalXS,
    },
    detailLabel: {
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground2,
      fontSize: tokens.fontSizeBase200,
    },
    detailValue: {
      color: tokens.colorNeutralForeground1,
      fontSize: tokens.fontSizeBase300,
    },
    approvalSection: {
      marginTop: tokens.spacingVerticalXL,
      paddingTop: tokens.spacingVerticalXL,
      borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    approvalTitle: {
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      marginBottom: tokens.spacingVerticalM,
    },
    commentInput: {
      marginBottom: tokens.spacingVerticalL,
    },
    commentLabel: {
      display: 'block',
      marginBottom: tokens.spacingVerticalS,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground1,
    },
    commentTextarea: {
      width: '100%',
    },
    dialogActions: {
      display: 'flex',
      gap: tokens.spacingHorizontalM,
      justifyContent: 'flex-end',
      padding: `${tokens.spacingVerticalL} ${tokens.spacingHorizontalXL}`,
    },
    dialogButton: {
      minWidth: '80px',
    },
    approveButton: {
      backgroundColor: tokens.colorPaletteGreenBackground2,
      color: tokens.colorPaletteGreenForeground2,
      '&:hover': {
        backgroundColor: tokens.colorPaletteGreenBackground3,
      },
    },
    rejectButton: {
      backgroundColor: tokens.colorPaletteRedBackground2,
      color: tokens.colorPaletteRedForeground2,
      '&:hover': {
        backgroundColor: tokens.colorPaletteRedBackground3,
      },
    },
    cancelButton: {
      backgroundColor: tokens.colorPaletteYellowBackground2,
      color: tokens.colorPaletteYellowForeground2,
      '&:hover': {
        backgroundColor: tokens.colorPaletteYellowBackground3,
      },
    },
    // Styles pour la légende
    legendSection: {
      backgroundColor: tokens.colorNeutralBackground1,
      borderRadius: tokens.borderRadiusLarge,
      padding: tokens.spacingVerticalL,
      marginBottom: tokens.spacingVerticalXL,
      boxShadow: tokens.shadow8,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    legendTitle: {
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
      marginBottom: tokens.spacingVerticalM,
      color: tokens.colorNeutralForeground1,
    },
    legendGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
      gap: tokens.spacingVerticalM,
    },
    legendCategory: {
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacingVerticalS,
    },
    legendCategoryTitle: {
      fontSize: tokens.fontSizeBase200,
      fontWeight: tokens.fontWeightSemibold,
      color: tokens.colorNeutralForeground2,
      marginBottom: tokens.spacingVerticalXS,
    },
    legendItem: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalS,
      fontSize: tokens.fontSizeBase200,
    },
    teamsLinkStyle: {
      color: tokens.colorBrandForeground1,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color: tokens.colorBrandForeground2,
      },
    },
    approvalResponseContainer: {
      display: 'flex',
      alignItems: 'center',
      padding: tokens.spacingVerticalM,
      borderRadius: tokens.borderRadiusMedium,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    approvalResponseApproved: {
      backgroundColor: tokens.colorPaletteGreenBackground1,
    },
    approvalResponseRejected: {
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
    approvalResponsePending: {
      backgroundColor: tokens.colorNeutralBackground3,
    },
    approvalResponseNotNeeded: {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    approvalResponderInfo: {
      flex: 1,
    },
    approvalResponderName: {
      display: 'flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalXS,
      marginBottom: tokens.spacingVerticalXS,
      fontSize: tokens.fontSizeBase300,
      fontWeight: tokens.fontWeightSemibold,
    },
    approvalResponderDetails: {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
      marginLeft: '26px',
    },
    approvalResponderComments: {
      marginTop: tokens.spacingVerticalXS,
      fontStyle: 'italic',
      color: tokens.colorNeutralForeground2,
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: tokens.spacingVerticalM,
      padding: tokens.spacingVerticalM,
      backgroundColor: tokens.colorNeutralBackground1,
      borderRadius: tokens.borderRadiusMedium,
    },
    paginationInfo: {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
    },
    filtersSummary: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: tokens.spacingVerticalM,
    },
    filtersSummaryText: {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
    },
    filtersItemsPerPageLabel: {
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2,
    },
});

export default function RequestsPage({ theme = "default" }: RequestsPageProps) {
  const styles = useStyles();
  const [requests, setRequests] = useState<TeamRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const [currentUserEmail, setCurrentUserEmail] = useState<string>("");
  const [isApprover, setIsApprover] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);

  // Filtres
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<RequestStatusFilter>("all");

  // Dialog d'approbation/rejet
  const [selectedRequest, setSelectedRequest] = useState<TeamRequest | null>(null);
  const [approvalAction, setApprovalAction] = useState<"approve" | "reject" | null>(null);
  const [comments, setComments] = useState("");
  const [processingAction, setProcessingAction] = useState<"approve" | "reject" | null>(null);

  // Dialog de confirmation d'annulation
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<string | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Notification d'erreur
  const [errorNotification, setErrorNotification] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
  }>({
    isOpen: false,
    title: "",
    message: ""
  });

  // Fonction helper pour afficher une notification d'erreur
  const showErrorNotification = (title: string, message: string) => {
    setErrorNotification({
      isOpen: true,
      title,
      message
    });
  };

  const closeErrorNotification = () => {
    setErrorNotification({
      ...errorNotification,
      isOpen: false
    });
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);

      // Récupérer le contexte Teams pour obtenir l'ID et l'email de l'utilisateur
      const context = await app.getContext();
      const userId = context.user?.id || "";
      const userEmail = context.user?.userPrincipalName || context.user?.loginHint || "";
      setCurrentUserId(userId);
      setCurrentUserEmail(userEmail);

      // Charger les settings pour déterminer si l'utilisateur est approbateur
      const settingsResp = await apiService.getSettings();
      if (settingsResp.success && settingsResp.data) {
        setSettings(settingsResp.data);

        // Vérifier si l'utilisateur est dans la liste des approbateurs
        const approvers = settingsResp.data.approvers || [];
        const userIsApprover = approvers.some(a => a.id === userId);
        setIsApprover(userIsApprover);
      }

      // Charger toutes les demandes
      await loadRequests();

      // 🔍 Vérifier si un requestId est passé dans le deep link
      // Vérifie plusieurs sources possibles
      let requestId: string | undefined;

      // 1. Vérifier les paramètres de requête
      const urlParams = new URLSearchParams(window.location.search);
      const urlRequestId = urlParams.get('requestId');
      if (urlRequestId) {
        requestId = urlRequestId;
        console.log("[RequestsPage] RequestId found in URL parameters:", requestId);
      }

      // 2. Vérifier le hash de l'URL
      if (!requestId && window.location.hash) {
        try {
          const hashParams = new URLSearchParams(window.location.hash.substring(1));
          const hashRequestId = hashParams.get('requestId');
          if (hashRequestId) {
            requestId = hashRequestId;
            console.log("[RequestsPage] RequestId found in URL hash:", requestId);
          }
        } catch (e) {
          console.warn("[RequestsPage] Error parsing URL hash:", e);
        }
      }

      // 3. Vérifier le contexte Teams (subPageId)
      if (!requestId && context.page?.subPageId) {
        requestId = context.page.subPageId;
        console.log("[RequestsPage] RequestId found in Teams context (subPageId):", requestId);
      }

      // 4. Vérifier le contexte Teams (frameContext)
      if (!requestId && context.page?.frameContext) {
        try {
          const frameContext = JSON.parse(context.page.frameContext as any);
          if (frameContext.subEntityId) {
            requestId = frameContext.subEntityId;
            console.log("[RequestsPage] RequestId found in Teams context (frameContext):", requestId);
          }
        } catch (e) {
          console.warn("[RequestsPage] Error parsing Teams frameContext:", e);
        }
      }

      // Si on a un requestId valide, charger la demande et ouvrir la modal
      if (requestId && requestId.trim().length > 0) {
        console.log("[RequestsPage] Looking for request with ID:", requestId);
        const resp = await apiService.getAllRequests();
        if (resp.success && resp.data) {
          const request = resp.data.find(r => r.id === requestId);
          if (request) {
            console.log("[RequestsPage] Opening modal for request:", request);
            setSelectedRequest(request);
            setApprovalAction(null);
          } else {
            console.warn("[RequestsPage] Request not found with ID:", requestId);
          }
        }
      }
    } catch (err: any) {
      console.error("Error initializing:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const loadRequests = async () => {
    try {
      const resp = await apiService.getAllRequests();
      if (resp.success && resp.data) {
        setRequests(resp.data);
      }
    } catch (err: any) {
      console.error("Error loading requests:", err);
      setError("Erreur lors du chargement des demandes");
    }
  };

  // Filtrer les demandes selon le rôle et les filtres
  const filteredRequests = useMemo(() => {
    let filtered = requests;

    // Si l'utilisateur n'est pas approbateur, afficher seulement ses demandes
    if (!isApprover && currentUserId) {
      filtered = filtered.filter(r => r.requesterId === currentUserId);
    }

    // Filtre par statut
    if (statusFilter !== "all") {
      filtered = filtered.filter(r => r.status === statusFilter);
    }

    // Recherche textuelle
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(r =>
        r.teamName.toLowerCase().includes(query) ||
        r.requesterEmail.toLowerCase().includes(query) ||
        r.description.toLowerCase().includes(query)
      );
    }

    // Trier par date (plus récent en premier)
    return filtered.sort((a, b) =>
      new Date(b.submittedDate).getTime() - new Date(a.submittedDate).getTime()
    );
  }, [requests, isApprover, currentUserId, statusFilter, searchQuery]);

  // Reset à la page 1 lors du changement de filtres
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchQuery]);

  // Pagination des données
  const paginatedData = useMemo(() => {
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const items = filteredRequests.slice(startIndex, endIndex);

    return {
      items,
      totalPages,
      totalItems: filteredRequests.length,
      startIndex: filteredRequests.length > 0 ? startIndex + 1 : 0,
      endIndex: Math.min(endIndex, filteredRequests.length)
    };
  }, [filteredRequests, currentPage, itemsPerPage]);

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newValue: number) => {
    setItemsPerPage(newValue);
    setCurrentPage(1);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;

    setProcessingAction('approve');
    try {
      const resp = await apiService.approveRequest(
        selectedRequest.id!,
        currentUserId,
        currentUserEmail,
        comments
      );
      if (resp.success) {
        await loadRequests();
        setSelectedRequest(null);
        setApprovalAction(null);
        setComments("");
      } else {
        showErrorNotification(
          "Erreur lors de l'approbation",
          resp.error || "Une erreur inconnue s'est produite. Veuillez réessayer."
        );
      }
    } catch (err: any) {
      console.error("Error approving request:", err);
      showErrorNotification(
        "Erreur lors de l'approbation",
        err?.message || "Une erreur inattendue s'est produite."
      );
    } finally {
      setProcessingAction(null);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;

    setProcessingAction('reject');
    try {
      const resp = await apiService.rejectRequest(
        selectedRequest.id!,
        currentUserId,
        currentUserEmail,
        comments
      );
      if (resp.success) {
        await loadRequests();
        setSelectedRequest(null);
        setApprovalAction(null);
        setComments("");
      } else {
        showErrorNotification(
          "Erreur lors du rejet",
          resp.error || "Une erreur inconnue s'est produite. Veuillez réessayer."
        );
      }
    } catch (err: any) {
      console.error("Error rejecting request:", err);
      showErrorNotification(
        "Erreur lors du rejet",
        err?.message || "Une erreur inattendue s'est produite."
      );
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCancelClick = (requestId: string) => {
    setRequestToCancel(requestId);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (!requestToCancel) return;

    try {
      setProcessingAction('approve'); // Use 'approve' for cancel action (reusing the state)
      const resp = await apiService.cancelRequest(requestToCancel, currentUserId);
      if (resp.success) {
        await loadRequests();
        setCancelDialogOpen(false);
        setRequestToCancel(null);
      } else {
        setError("Erreur lors de l'annulation: " + (resp.error || "Erreur inconnue"));
      }
    } catch (err: any) {
      console.error("Error canceling request:", err);
      setError("Erreur lors de l'annulation");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
    setRequestToCancel(null);
  };

  const getStatusBadge = (status: RequestStatus) => {
    const colors: Record<RequestStatus, "success" | "danger" | "warning" | "informative" | "subtle"> = {
      [RequestStatus.Pending]: "warning",
      [RequestStatus.Approved]: "success",
      [RequestStatus.Rejected]: "danger",
      [RequestStatus.Created]: "success",
      [RequestStatus.CreatingTeam]: "informative",
      [RequestStatus.Completed]: "success",
      [RequestStatus.Failed]: "danger",
      [RequestStatus.Cancelled]: "subtle",
    };

    const labels: Record<RequestStatus, string> = {
      [RequestStatus.Pending]: "En attente",
      [RequestStatus.Approved]: "Approuvé",
      [RequestStatus.Rejected]: "Rejeté",
      [RequestStatus.Created]: "Créé",
      [RequestStatus.CreatingTeam]: "Création en cours",
      [RequestStatus.Completed]: "Terminé",
      [RequestStatus.Failed]: "Échec",
      [RequestStatus.Cancelled]: "Annulé",
    };

    return <Badge appearance="filled" color={colors[status]} style={{ whiteSpace: 'nowrap' }}>{labels[status]}</Badge>;
  };

  const getPrivacyLabel = (privacy: string) => {
    const labels: Record<string, string> = {
      'private': '🔒 Privée',
      'public': '🌐 Publique',
    };
    return labels[privacy] || privacy;
  };

  const getApprovalProgress = (item: TeamRequest) => {
    // Pour les anciennes demandes sans approvalResponses, afficher "—" ou l'info de base
    if (!item.approvalResponses || item.approvalResponses.length === 0) {
      // Rétrocompatibilité: si la demande est approuvée/rejetée mais sans détails
      if (item.status === RequestStatus.Approved) {
        return { display: '✓ Approuvé', tooltip: `Approuvé par ${item.approverName || item.approverEmail || 'Inconnu'}` };
      }
      if (item.status === RequestStatus.Rejected) {
        return { display: '✗ Rejeté', tooltip: `Rejeté par ${item.approverName || item.approverEmail || 'Inconnu'}` };
      }
      return { display: '—', tooltip: 'Pas de suivi disponible' };
    }

    const responses = item.approvalResponses;
    const approvedCount = responses.filter(r => r.response === 'approved').length;
    const rejectedCount = responses.filter(r => r.response === 'rejected').length;
    const pendingCount = responses.filter(r => r.response === 'pending').length;
    const totalCount = responses.length;

    // Pour le mode "basic", seul le premier approbateur compte
    // On vérifie si on peut détecter le mode via les settings
    const isBasicAwaitAll = settings?.notificationSettings?.approvalMode === 'basicAwaitAll';

    if (!isBasicAwaitAll && totalCount > 0) {
      // Mode "basic" - montrer seulement l'approbateur qui a répondu
      if (approvedCount > 0) {
        const approver = responses.find(r => r.response === 'approved');
        return {
          display: `✓ ${approver?.approverName || approver?.approverEmail || 'Approuvé'}`,
          tooltip: `Approuvé par ${approver?.approverName || approver?.approverEmail || 'Inconnu'}`
        };
      }
      if (rejectedCount > 0) {
        const rejecter = responses.find(r => r.response === 'rejected');
        return {
          display: `✗ ${rejecter?.approverName || rejecter?.approverEmail || 'Rejeté'}`,
          tooltip: `Rejeté par ${rejecter?.approverName || rejecter?.approverEmail || 'Inconnu'}`
        };
      }
      if (pendingCount > 0) {
        return {
          display: `⏳ En attente`,
          tooltip: `En attente de réponse de ${pendingCount} approbateur(s)`
        };
      }
    }

    // Mode "basicAwaitAll" - montrer la progression
    if (approvedCount === totalCount) {
      return { display: `✓ ${approvedCount}/${totalCount}`, tooltip: `Tous les approbateurs ont approuvé (${approvedCount}/${totalCount})` };
    }
    if (rejectedCount > 0) {
      return { display: `✗ ${rejectedCount}/${totalCount}`, tooltip: `${rejectedCount} rejet(s) sur ${totalCount} approbateur(s)` };
    }
    if (approvedCount > 0) {
      // Si la demande est déjà approuvée ou terminée, les réponses en attente ne sont plus nécessaires
      const isFinalized = item.status === RequestStatus.Approved || item.status === RequestStatus.Completed || item.status === RequestStatus.Created;
      return {
        display: `${isFinalized ? '✓' : '⏳'} ${approvedCount}/${totalCount}`,
        tooltip: isFinalized
          ? `Demande approuvée avec ${approvedCount} réponse(s) positive(s) (${pendingCount} réponse(s) non nécessaire(s))`
          : `${approvedCount} approbation(s), ${pendingCount} en attente sur ${totalCount}`
      };
    }
    return {
      display: `⏳ 0/${totalCount}`,
      tooltip: `En attente de ${totalCount} approbateur(s)`
    };
  };

  const columns: TableColumnDefinition<TeamRequest>[] = useMemo(() => [
    createTableColumn<TeamRequest>({
      columnId: "teamName",
      compare: (a, b) => a.teamName.localeCompare(b.teamName),
      renderHeaderCell: () => "Nom de l'équipe",
      renderCell: (item) => (
        <TableCellLayout truncate title={item.teamName}>
          <Button
            appearance="transparent"
            size="small"
            onClick={() => {
              setSelectedRequest(item);
              setApprovalAction(null);
            }}
            className={styles.detailsActionButton}
          >
            {item.teamName}
          </Button>
        </TableCellLayout>
      ),
    }),
    createTableColumn<TeamRequest>({
      columnId: "privacy",
      compare: (a, b) => a.privacy.localeCompare(b.privacy),
      renderHeaderCell: () => "Type",
      renderCell: (item) => (
        <TableCellLayout truncate title={getPrivacyLabel(item.privacy)}>
          {getPrivacyLabel(item.privacy)}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TeamRequest>({
      columnId: "actions",
      renderHeaderCell: () => "Actions",
      renderCell: (item) => (
        <TableCellLayout>
          <div className={styles.actionButtonsContainer}>
            {/* Actions pour les approbateurs - uniquement pour les demandes en attente */}
            {isApprover && item.status === RequestStatus.Pending && (
              <Button
                size="small"
                appearance="subtle"
                onClick={() => {
                  setSelectedRequest(item);
                  setApprovalAction(null);
                }}
                className={styles.detailsActionButton}
              >
                <CheckmarkCircle20Regular className="icon-text" />
                <DismissCircle20Regular className="icon-text" />
              </Button>
            )}

            {/* Action pour les demandeurs (annuler leur propre demande) - uniquement pour les demandes en attente */}
            {!isApprover && item.requesterId === currentUserId && item.status === RequestStatus.Pending && (
              <Tooltip content="Annuler la demande" relationship="label">
                <Button
                  size="small"
                  appearance="subtle"
                  icon={<DismissCircle20Regular />}
                  onClick={() => handleCancelClick(item.id!)}
                  className={styles.cancelActionButton}
                >
                  Annuler
                </Button>
              </Tooltip>
            )}

            {/* Pas d'actions pour les demandes approuvées ou rejetées */}
            {(item.status === RequestStatus.Approved || item.status === RequestStatus.Rejected || item.status === RequestStatus.Cancelled) && (
              <span style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
                —
              </span>
            )}
          </div>
        </TableCellLayout>
      ),
   }),
    createTableColumn<TeamRequest>({
      columnId: "requester",
      compare: (a, b) => a.requesterEmail.localeCompare(b.requesterEmail),
      renderHeaderCell: () => "Demandeur",
      renderCell: (item) => (
        <TableCellLayout truncate title={item.requesterEmail}>
          {item.requesterEmail}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TeamRequest>({
      columnId: "description",
      compare: (a, b) => a.description.localeCompare(b.description),
      renderHeaderCell: () => "Description",
      renderCell: (item) => (
        <TableCellLayout truncate title={item.description}>
          {item.description}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TeamRequest>({
      columnId: "status",
      compare: (a, b) => a.status.localeCompare(b.status),
      renderHeaderCell: () => "Statut",
      renderCell: (item) => (
        <TableCellLayout>
          {getStatusBadge(item.status)}
        </TableCellLayout>
      ),
    }),
    createTableColumn<TeamRequest>({
      columnId: "teamsStatus",
      renderHeaderCell: () => "État Teams",
      renderCell: (item) => {
        // Si l'équipe a été créée avec succès (on a un createdTeamId)
        if (item.createdTeamId) {
          return (
            <TableCellLayout>
              <Tooltip content={`Équipe créée (ID: ${item.createdTeamId})`} relationship="label">
                <Badge appearance="tint" color="success" style={{ cursor: 'help', whiteSpace: 'nowrap' }}>
                  ✅ Provisionnée
                </Badge>
              </Tooltip>
            </TableCellLayout>
          );
        }
        // Demande approuvée mais équipe pas encore créée
        if (item.status === RequestStatus.Approved) {
          return (
            <TableCellLayout>
              <Badge appearance="tint" color="warning" style={{ whiteSpace: 'nowrap' }}>
                ⏳ En attente
              </Badge>
            </TableCellLayout>
          );
        }
        // Demande en attente d'approbation
        if (item.status === RequestStatus.Pending) {
          return (
            <TableCellLayout>
              <Badge appearance="outline" style={{ whiteSpace: 'nowrap' }}>
                ⏸️ Approbation
              </Badge>
            </TableCellLayout>
          );
        }
        // Échec de création
        if (item.status === RequestStatus.Failed) {
          return (
            <TableCellLayout>
              <Tooltip content={item.errorMessage || "Erreur lors de la création"} relationship="label">
                <Badge appearance="tint" color="danger" style={{ cursor: 'help', whiteSpace: 'nowrap' }}>
                  ❌ Échec
                </Badge>
              </Tooltip>
            </TableCellLayout>
          );
        }
        // Demande rejetée ou annulée
        return (
          <TableCellLayout>
            <Badge appearance="outline" style={{ whiteSpace: 'nowrap' }}>
              — N/A
            </Badge>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<TeamRequest>({
      columnId: "approvals",
      renderHeaderCell: () => "Approbations",
      renderCell: (item) => {
        const progress = getApprovalProgress(item);
        return (
          <TableCellLayout>
            <Tooltip content={progress.tooltip} relationship="label">
              <span style={{ cursor: 'help' }}>{progress.display}</span>
            </Tooltip>
          </TableCellLayout>
        );
      },
    }),
    createTableColumn<TeamRequest>({
      columnId: "date",
      compare: (a, b) => new Date(a.submittedDate).getTime() - new Date(b.submittedDate).getTime(),
      renderHeaderCell: () => "Date",
      renderCell: (item) => (
        <TableCellLayout>
          {new Date(item.submittedDate).toLocaleDateString('fr-FR')}
        </TableCellLayout>
      ),
    }),
  ], [isApprover, currentUserId, handleCancelClick, setSelectedRequest, setApprovalAction, getStatusBadge, getPrivacyLabel, getApprovalProgress]);

  if (loading) {
    return (
      <div className={styles.root}>
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner}>
            <Spinner size="large" />
          </div>
          <div className={styles.loadingText}>Chargement des demandes...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.header}>
          <h1 className={styles.headerTitle}>📋 Demandes</h1>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorMessage}>⚠️ {error}</div>
          <Button appearance="primary" onClick={initialize}>
            Réessayer
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {/* En-tête */}
      <div className={styles.header}>
        <h1 className={styles.headerTitle}>
          {isApprover ? "Demandes à traiter" : "Mes demandes"}
        </h1>
        <p className={styles.headerSubtitle}>
          {isApprover
            ? "Gérez les demandes d'équipes de vos collaborateurs"
            : "Suivez l'état de vos demandes d'équipes"}
        </p>
      </div>

      {/* Contenu principal */}
      {!loading && !error && (
        <>

          {/* Filtres */}
          <div className={styles.filtersSection}>
            <div className={styles.filtersContainer}>
              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>Rechercher</label>
                <SearchBox
                  placeholder="Rechercher par nom d'équipe, email..."
                  value={searchQuery}
                  onChange={(_, data) => setSearchQuery(data?.value || "")}
                  style={{ width: "100%" }}
                />
              </div>
              <div className={styles.filterItem}>
                <label className={styles.filterLabel}>Statut</label>
                <Dropdown
                  placeholder="Tous les statuts"
                  value={statusFilter === "all" ? "Tous les statuts" : statusFilter}
                  onOptionSelect={(_, data) => setStatusFilter(data.optionValue as RequestStatusFilter)}
                  style={{ minWidth: "180px" }}
                >
                  <Option value="all">Tous les statuts</Option>
                  <Option value={RequestStatus.Pending}>En attente</Option>
                  <Option value={RequestStatus.Approved}>Approuvé</Option>
                  <Option value={RequestStatus.CreatingTeam}>Création en cours</Option>
                  <Option value={RequestStatus.Created}>Créé</Option>
                  <Option value={RequestStatus.Completed}>Terminé</Option>
                  <Option value={RequestStatus.Rejected}>Rejeté</Option>
                  <Option value={RequestStatus.Failed}>Échec</Option>
                  <Option value={RequestStatus.Cancelled}>Annulé</Option>
                </Dropdown>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: tokens.spacingVerticalL }}>
              <p className={styles.filtersSummaryText}>
                {paginatedData.totalItems > 0
                  ? `Affichage de ${paginatedData.startIndex} à ${paginatedData.endIndex} sur ${paginatedData.totalItems} demande${paginatedData.totalItems > 1 ? 's' : ''}`
                  : 'Aucune demande trouvée'}
              </p>
              {paginatedData.totalItems > 0 && (
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'center' }}>
                  <label className={styles.filtersItemsPerPageLabel}>
                    Éléments par page:
                  </label>
                  <Dropdown
                    value={String(itemsPerPage)}
                    onOptionSelect={(_, data) => handleItemsPerPageChange(Number(data.optionValue))}
                    style={{ minWidth: '80px' }}
                  >
                    <Option value="10">10</Option>
                    <Option value="25">25</Option>
                    <Option value="50">50</Option>
                    <Option value="100">100</Option>
                  </Dropdown>
                </div>
              )}
            </div>
          </div>

          {/* DataGrid */}
          {paginatedData.items.length > 0 ? (
            <div className={styles.datagridContainer}>
              <div className={styles.datagridWrapper}>
                <DataGrid
                  items={paginatedData.items}
                  columns={columns}
                  sortable
                  resizableColumns
                  getRowId={(item) => item.id!}
                  style={{ width: '100%', minWidth: '100%', borderCollapse: 'collapse' }}
                  columnSizingOptions={{
                    teamName: {
                      minWidth: 150,
                      defaultWidth: 200
                    },
                    privacy: {
                      minWidth: 110,
                      defaultWidth: 130
                    },
                    actions: {
                      minWidth: 150,
                      defaultWidth: 180
                    },
                    requester: {
                      minWidth: 180,
                      defaultWidth: 240
                    },
                    description: {
                      minWidth: 160,
                      defaultWidth: 220
                    },
                    status: {
                      minWidth: 80,
                      defaultWidth: 95
                    },
                    teamsStatus: {
                      minWidth: 130,
                      defaultWidth: 150
                    },
                    approvals: {
                      minWidth: 120,
                      defaultWidth: 150
                    },
                    date: {
                      minWidth: 80,
                      defaultWidth: 95
                    }
                  }}
                >
                  <DataGridHeader>
                    <DataGridRow>
                      {({ renderHeaderCell }) => (
                        <DataGridHeaderCell className={styles.datagridHeaderCell}>
                          {renderHeaderCell()}
                        </DataGridHeaderCell>
                      )}
                    </DataGridRow>
                  </DataGridHeader>
                  <DataGridBody>
                    {({ item, rowId }) => (
                      <DataGridRow key={rowId} className={styles.rowHover}>
                        {({ renderCell }) => (
                          <DataGridCell className={styles.datagridCell}>
                            {renderCell(item)}
                          </DataGridCell>
                        )}
                      </DataGridRow>
                    )}
                  </DataGridBody>
                </DataGrid>
              </div>

              {/* Contrôles de pagination */}
              {paginatedData.totalPages > 1 && (
                <div className={styles.paginationContainer}>
                  <div className={styles.paginationInfo}>
                    Page {currentPage} sur {paginatedData.totalPages}
                  </div>
                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
                    <Tooltip content="Première page" relationship="label">
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(1)}
                        icon={<ChevronDoubleLeft20Regular />}
                      />
                    </Tooltip>
                    <Tooltip content="Page précédente" relationship="label">
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={currentPage === 1}
                        onClick={() => handlePageChange(currentPage - 1)}
                        icon={<ChevronLeft20Regular />}
                      />
                    </Tooltip>
                    <Tooltip content="Page suivante" relationship="label">
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={currentPage === paginatedData.totalPages}
                        onClick={() => handlePageChange(currentPage + 1)}
                        icon={<ChevronRight20Regular />}
                      />
                    </Tooltip>
                    <Tooltip content="Dernière page" relationship="label">
                      <Button
                        size="small"
                        appearance="subtle"
                        disabled={currentPage === paginatedData.totalPages}
                        onClick={() => handlePageChange(paginatedData.totalPages)}
                        icon={<ChevronDoubleRight20Regular />}
                      />
                    </Tooltip>
                  </div>
                </div>
              )}

              {/* Légende des statuts */}
              <div className={styles.legendSection}>
                <div className={styles.legendTitle}>📖 Légende</div>
                <div className={styles.legendGrid}>
                  {/* Statut de la demande */}
                  <div className={styles.legendCategory}>
                    <div className={styles.legendCategoryTitle}>Statut de la demande</div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="warning">En attente</Badge>
                      <span>Demande en attente d'approbation</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="success">Approuvé</Badge>
                      <span>Demande approuvée</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="informative">Création en cours</Badge>
                      <span>Équipe en cours de création</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="success">Créé</Badge>
                      <span>Équipe créée avec succès</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="success">Terminé</Badge>
                      <span>Processus complété</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="danger">Rejeté</Badge>
                      <span>Demande rejetée</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="danger">Échec</Badge>
                      <span>Erreur lors de la création</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="filled" color="subtle">Annulé</Badge>
                      <span>Demande annulée</span>
                    </div>
                  </div>

                  {/* État Teams */}
                  <div className={styles.legendCategory}>
                    <div className={styles.legendCategoryTitle}>État Teams</div>
                    <div className={styles.legendItem}>
                      <Badge appearance="tint" color="success" style={{ whiteSpace: 'nowrap' }}>✅ Provisionnée</Badge>
                      <span>Équipe créée dans Teams</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="tint" color="warning" style={{ whiteSpace: 'nowrap' }}>⏳ En attente</Badge>
                      <span>Création en cours</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="outline" style={{ whiteSpace: 'nowrap' }}>⏸️ Approbation</Badge>
                      <span>En attente d'approbation</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="tint" color="danger" style={{ whiteSpace: 'nowrap' }}>❌ Échec</Badge>
                      <span>Erreur de création</span>
                    </div>
                    <div className={styles.legendItem}>
                      <Badge appearance="outline" style={{ whiteSpace: 'nowrap' }}>— N/A</Badge>
                      <span>Non applicable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📭</div>
              <h3 className={styles.emptyTitle}>
                Aucune demande trouvée
              </h3>
              <p className={styles.emptyMessage}>
                {searchQuery || statusFilter !== "all"
                  ? "Essayez de modifier vos critères de recherche"
                  : isApprover
                    ? "Aucune demande n'a encore été soumise"
                    : "Vous n'avez pas encore créé de demande"}
              </p>
            </div>
          )}
        </>
      )}

      {/* Modal de détails */}
      <Dialog open={!!selectedRequest} onOpenChange={(_, { open }) => { if (!open) { setSelectedRequest(null); setApprovalAction(null); setComments(""); } }}>
        <DialogSurface className={styles.dialogSurface}>
          <DialogBody>
            <DialogTitle className={styles.dialogTitle}>
              {approvalAction === "approve" && "Approuver la demande"}
              {approvalAction === "reject" && "Rejeter la demande"}
              {!approvalAction && "Détails de la demande"}
            </DialogTitle>
            <DialogContent className={styles.dialogContent}>
              {selectedRequest && (
                <div className={styles.detailsGrid}>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Nom de l'équipe</div>
                    <div className={styles.detailValue}>{selectedRequest.teamName}</div>
                  </div>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Type</div>
                    <div className={styles.detailValue}>{getPrivacyLabel(selectedRequest.privacy)}</div>
                  </div>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Demandeur</div>
                    <div className={styles.detailValue}>{selectedRequest.requesterEmail}</div>
                  </div>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Email</div>
                    <div className={styles.detailValue}>{selectedRequest.requesterEmail}</div>
                  </div>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Statut</div>
                    <div className={styles.detailValue}>{getStatusBadge(selectedRequest.status)}</div>
                  </div>
                  <div className={styles.detailItem}>
                    <div className={styles.detailLabel}>Date de création</div>
                    <div className={styles.detailValue}>
                      {new Date(selectedRequest.submittedDate).toLocaleDateString('fr-FR')}
                    </div>
                  </div>
                  {selectedRequest.description && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>Description</div>
                      <div className={styles.detailValue}>{selectedRequest.description}</div>
                    </div>
                  )}
                  {selectedRequest.justification && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>Justification</div>
                      <div className={styles.detailValue}>{selectedRequest.justification}</div>
                    </div>
                  )}
                  {selectedRequest.approverComments && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>Commentaires de l'approbateur</div>
                      <div className={styles.detailValue}>{selectedRequest.approverComments}</div>
                    </div>
                  )}
                  {selectedRequest.approverName && (
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>Traité par</div>
                      <div className={styles.detailValue}>{selectedRequest.approverName}</div>
                    </div>
                  )}
                  {selectedRequest.processedDate && (
                    <div className={styles.detailItem}>
                      <div className={styles.detailLabel}>Date de traitement</div>
                      <div className={styles.detailValue}>
                        {new Date(selectedRequest.processedDate).toLocaleDateString('fr-FR')}
                      </div>
                    </div>
                  )}

                  {/* Informations sur l'équipe Teams créée */}
                  {selectedRequest.createdTeamId && (
                    <>
                      <div className={styles.detailItemFull}>
                        <div className={styles.detailLabel}>État de l'équipe Teams</div>
                        <div className={styles.detailValue}>
                          {selectedRequest.status === RequestStatus.Failed ? (
                            <Badge appearance="tint" color="danger" size="large">
                              ❌ Échec de la création
                            </Badge>
                          ) : (
                            <Badge appearance="tint" color="success" size="large">
                              ✅ Équipe provisionnée avec succès
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className={styles.detailItem}>
                        <div className={styles.detailLabel}>ID de l'équipe Teams</div>
                        <div className={styles.detailValue} style={{
                          fontFamily: 'monospace',
                          fontSize: tokens.fontSizeBase200,
                          wordBreak: 'break-all'
                        }}>
                          {selectedRequest.createdTeamId}
                        </div>
                      </div>
                      <div className={styles.detailItem}>
                        <div className={styles.detailLabel}>Lien Teams</div>
                        <div className={styles.detailValue}>
                          <a
                            href={`https://teams.microsoft.com/l/team/${selectedRequest.createdTeamId}/conversations?groupId=${selectedRequest.createdTeamId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => {
                              e.preventDefault();
                              app.openLink(`https://teams.microsoft.com/l/team/${selectedRequest.createdTeamId}/conversations?groupId=${selectedRequest.createdTeamId}`);
                            }}
                            style={{
                              color: tokens.colorBrandForeground1,
                              textDecoration: 'underline',
                              cursor: 'pointer'
                            }}
                          >
                            🔗 Ouvrir dans Teams
                          </a>
                        </div>
                      </div>
                    </>
                  )}

                  {/* Message pour les demandes en attente */}
                  {!selectedRequest.createdTeamId && selectedRequest.status === RequestStatus.Approved && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>État de l'équipe Teams</div>
                      <div className={styles.detailValue}>
                        <Badge appearance="tint" color="warning" size="large">
                          ⏳ En attente de création automatique
                        </Badge>
                      </div>
                    </div>
                  )}

                  {!selectedRequest.createdTeamId && selectedRequest.status === RequestStatus.Pending && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>État de l'équipe Teams</div>
                      <div className={styles.detailValue}>
                        <Badge appearance="outline" size="large">
                          ⏸️ En attente d'approbation
                        </Badge>
                      </div>
                    </div>
                  )}

                  {selectedRequest.errorMessage && (
                    <div className={styles.detailItemFull}>
                      <div className={styles.detailLabel}>Message d'erreur</div>
                      <div className={styles.detailValue} style={{
                        color: tokens.colorPaletteRedForeground1,
                        backgroundColor: tokens.colorPaletteRedBackground1,
                        padding: tokens.spacingVerticalM,
                        borderRadius: tokens.borderRadiusMedium,
                        border: `1px solid ${tokens.colorPaletteRedBorder1}`
                      }}>
                        {selectedRequest.errorMessage}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section de suivi des approbations */}
              {selectedRequest?.approvalResponses && selectedRequest.approvalResponses.length > 0 && (
                <div className={styles.approvalSection}>
                  <div className={styles.approvalTitle}>Suivi des Approbations</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
                    {selectedRequest.approvalResponses.map((response, index) => {
                      // Si la demande est déjà approuvée et que cette réponse est en attente, afficher "Non nécessaire"
                      const isPendingButNotNeeded = selectedRequest.status === RequestStatus.Approved && response.response === 'pending';

                      let containerClass = styles.approvalResponseContainer;
                      if (response.response === 'approved') {
                        containerClass += ` ${styles.approvalResponseApproved}`;
                      } else if (response.response === 'rejected') {
                        containerClass += ` ${styles.approvalResponseRejected}`;
                      } else if (isPendingButNotNeeded) {
                        containerClass += ` ${styles.approvalResponseNotNeeded}`;
                      } else {
                        containerClass += ` ${styles.approvalResponsePending}`;
                      }

                      return (
                        <div key={index} className={containerClass}>
                          <div className={styles.approvalResponderInfo}>
                            <div className={styles.approvalResponderName}>
                              <span style={{ fontSize: '18px' }}>
                                {response.response === 'approved' ? '✅' :
                                 response.response === 'rejected' ? '❌' :
                                 isPendingButNotNeeded ? 'ℹ️' :
                                 '⏳'}
                              </span>
                              <strong>
                                {response.approverName || response.approverEmail}
                              </strong>
                            </div>
                            <div className={styles.approvalResponderDetails}>
                              <div>
                                {response.response === 'approved' ? 'Approuvé' :
                                 response.response === 'rejected' ? 'Rejeté' :
                                 isPendingButNotNeeded ? 'Non nécessaire' :
                                 'En attente'}
                                {response.responseDate && ` • ${new Date(response.responseDate).toLocaleString('fr-FR')}`}
                              </div>
                              {response.comments && (
                                <div className={styles.approvalResponderComments}>
                                  "{response.comments}"
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isApprover && selectedRequest?.status === RequestStatus.Pending && (() => {
                // Vérifier si l'utilisateur connecté a déjà donné sa réponse
                const userResponse = selectedRequest.approvalResponses?.find(
                  r => r.approverId === currentUserId
                );
                const hasAlreadyResponded = userResponse && userResponse.response !== 'pending';

                if (hasAlreadyResponded) {
                  const userResponseClass = `${styles.approvalResponseContainer} ${
                    userResponse.response === 'approved'
                      ? styles.approvalResponseApproved
                      : styles.approvalResponseRejected
                  }`;
                  
                  return (
                    <div className={styles.approvalSection}>
                      <div className={styles.approvalTitle}>Votre réponse</div>
                      <div className={userResponseClass}>
                        <div className={styles.approvalResponderInfo}>
                          <div className={styles.approvalResponderName}>
                            <span style={{ fontSize: '18px' }}>
                              {userResponse.response === 'approved' ? '✅' : '❌'}
                            </span>
                            <strong>
                              {userResponse.response === 'approved' ? 'Vous avez approuvé cette demande' : 'Vous avez rejeté cette demande'}
                            </strong>
                          </div>
                          {userResponse.responseDate && (
                            <div className={styles.approvalResponderDetails}>
                              {new Date(userResponse.responseDate).toLocaleString('fr-FR')}
                            </div>
                          )}
                          {userResponse.comments && (
                            <div className={styles.approvalResponderComments}>
                              "{userResponse.comments}"
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className={styles.approvalSection}>
                    <div className={styles.approvalTitle}>Actions d'approbation</div>
                    <div className={styles.commentInput}>
                      <label className={styles.commentLabel}>Commentaires (optionnel)</label>
                      <Textarea
                        value={comments}
                        onChange={(_, data) => setComments(data?.value || "")}
                        placeholder="Ajoutez un commentaire..."
                        rows={3}
                        className={styles.commentTextarea}
                      />
                    </div>
                  </div>
                );
              })()}

            </DialogContent>
            <DialogActions className={styles.dialogActions}>
              <Button
                appearance="secondary"
                onClick={() => {
                  setSelectedRequest(null);
                  setApprovalAction(null);
                  setComments("");
                }}
                className={styles.dialogButton}
              >
                Fermer
              </Button>
              {isApprover && selectedRequest?.status === RequestStatus.Pending && (() => {
                // Vérifier si l'utilisateur connecté a déjà donné sa réponse
                const userResponse = selectedRequest.approvalResponses?.find(
                  r => r.approverId === currentUserId
                );
                const hasAlreadyResponded = userResponse && userResponse.response !== 'pending';

                // Ne pas afficher les boutons si l'utilisateur a déjà répondu
                if (hasAlreadyResponded) {
                  return null;
                }

                return (
                  <>
                    <Button
                      appearance="secondary"
                      onClick={handleReject}
                      disabled={processingAction !== null}
                      className={styles.rejectButton}
                      icon={processingAction === 'reject' ? <Spinner size="tiny" /> : undefined}
                    >
                      {processingAction === 'reject' ? "Traitement..." : "Rejeter"}
                    </Button>
                    <Button
                      appearance="primary"
                      onClick={handleApprove}
                      disabled={processingAction !== null}
                      className={styles.approveButton}
                      icon={processingAction === 'approve' ? <Spinner size="tiny" /> : undefined}
                    >
                      {processingAction === 'approve' ? "Traitement..." : "Approuver"}
                    </Button>
                  </>
                );
              })()}
              {!isApprover && selectedRequest?.requesterId === currentUserId && selectedRequest?.status === RequestStatus.Pending && (
                <Button
                  appearance="secondary"
                  onClick={() => handleCancelClick(selectedRequest.id!)}
                  disabled={processingAction !== null}
                  className={styles.cancelButton}
                >
                  Annuler la demande
                </Button>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Dialog de confirmation d'annulation */}
      <Dialog open={cancelDialogOpen} onOpenChange={(_, data) => data.open ? null : handleCancelDialogClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Confirmer l'annulation</DialogTitle>
            <DialogContent>
              <p>Êtes-vous sûr de vouloir annuler cette demande ?</p>
              <p style={{ marginTop: '8px', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
                Cette action est irréversible.
              </p>
            </DialogContent>
            <DialogActions>
              <Button
                appearance="secondary"
                onClick={handleCancelDialogClose}
                disabled={processingAction !== null}
              >
                Retour
              </Button>
              <Button
                appearance="primary"
                onClick={handleCancelConfirm}
                disabled={processingAction !== null}
                style={{
                  backgroundColor: tokens.colorPaletteRedBackground3,
                  color: tokens.colorNeutralForegroundOnBrand,
                }}
                icon={processingAction === 'approve' ? <Spinner size="tiny" /> : undefined}
              >
                {processingAction === 'approve' ? "Traitement..." : "Confirmer l'annulation"}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Modal de notification d'erreur */}
      {errorNotification.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 999999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: tokens.colorNeutralBackground1,
            borderRadius: tokens.borderRadiusLarge,
            boxShadow: tokens.shadow16,
            border: `1px solid ${tokens.colorNeutralStroke1}`,
            padding: tokens.spacingVerticalXXXL,
            maxWidth: '500px',
            width: '90%',
          }}>
            <div style={{
              display: 'flex',
              gap: tokens.spacingHorizontalL,
              alignItems: 'flex-start',
            }}>
              <div style={{
                fontSize: '32px',
                flexShrink: 0,
              }}>
                ❌
              </div>
              <div style={{
                flex: 1,
              }}>
                <h3 style={{
                  margin: `0 0 ${tokens.spacingVerticalS} 0`,
                  fontSize: tokens.fontSizeBase300,
                  fontWeight: tokens.fontWeightSemibold,
                  color: tokens.colorPaletteRedForeground1,
                }}>
                  {errorNotification.title}
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: tokens.fontSizeBase200,
                  color: tokens.colorNeutralForeground2,
                  lineHeight: tokens.lineHeightBase300,
                }}>
                  {errorNotification.message}
                </p>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: tokens.spacingHorizontalM,
              justifyContent: 'flex-end',
              marginTop: tokens.spacingVerticalXL,
            }}>
              <Button
                appearance="primary"
                onClick={closeErrorNotification}
                style={{
                  minWidth: '100px',
                }}
              >
                Fermer
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
